/**
 * useOrgBranding — reads org logo + name from the already-loaded AuthContext profile.
 *
 * No extra fetch: the AuthContext fetchProfile() already selects
 *   `organizations!users_org_id_fkey(name, custom_fields)`
 * so the data is available synchronously once the profile resolves.
 *
 * Namespace priority for logo_url:
 *   1. custom_fields.branding.logo_url   — canonical (super-admin saves here post-fix)
 *   2. custom_fields.appearance.logo_url — back-compat (anything saved before the fix)
 *   3. null                              — no logo; caller renders the default HG asset
 *
 * orgName falls back to 'Homeschool Global' when the org name is unavailable.
 */
import { useAuth } from '../context/AuthContext';

const DEFAULT_ORG_NAME = 'Homeschool Global';

export interface OrgBranding {
  /** Absolute URL for the org logo, or null when not configured. */
  logoUrl: string | null;
  /**
   * URL (or data URI) for the browser-tab favicon, or null when not configured.
   * Same namespace priority as logo_url (branding.favicon_url wins over
   * appearance.favicon_url). Null → callers keep the static /favicon.ico.
   */
  faviconUrl: string | null;
  /** Display name for the organisation, e.g. for <img alt> and document.title. */
  orgName: string;
}

export function useOrgBranding(): OrgBranding {
  const { profile } = useAuth();

  const orgName = profile?.organization?.name ?? DEFAULT_ORG_NAME;

  const customFields: Record<string, any> = profile?.organization?.custom_fields ?? {};
  const brandingBlock: Record<string, any> = customFields?.branding ?? {};
  const appearanceBlock: Record<string, any> = customFields?.appearance ?? {};

  // branding.logo_url wins over appearance.logo_url; both must be a non-empty string.
  const logoUrl: string | null =
    (typeof brandingBlock.logo_url === 'string' && brandingBlock.logo_url) ||
    (typeof appearanceBlock.logo_url === 'string' && appearanceBlock.logo_url) ||
    null;

  // Same priority for the tab favicon.
  const faviconUrl: string | null =
    (typeof brandingBlock.favicon_url === 'string' && brandingBlock.favicon_url) ||
    (typeof appearanceBlock.favicon_url === 'string' && appearanceBlock.favicon_url) ||
    null;

  return { logoUrl, faviconUrl, orgName };
}
